import { UUID } from "@bitory-gmbh/nidavellir";
import { backendApi } from "@/services/backend/api";
import {
  providesEntity,
  providesList,
} from "@/services/backend/_lib/tag-helpers";
import { AirMicrobialSampleSet } from "@/services/backend/samples/types/air-microbial-sample";
import { Filter } from "@/shared/lib/filter/filter";
import {
  BaseSamplePatchRequest,
  NewSampleRequest,
} from "@/services/backend/samples/types/sample";
import { EntityId } from "@/shared/types/entity-id";
import { updateCache } from "@/services/backend/_lib/update-cache";

export const samplesAirMicrobialApi = backendApi
  .enhanceEndpoints({
    addTagTypes: ["SamplesAirMicrobialSampleSet"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      /**
       * Creates a sample set with the defaults of the company's base data.
       * Fails with 404 if the company has no base data default for the
       * kind. Use the PUT endpoint to update a sample set.
       */
      samplesAirMicrobialSampleSetCreate: build.mutation<
        AirMicrobialSampleSet,
        CreateRequest
      >({
        query: (request) => ({
          url: `/samples/air-microbial`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: [
          "SamplesAirMicrobialSampleSet",
          "SamplesSampleViewModel",
        ],
      }),
      samplesAirMicrobialSampleSetPut: build.mutation<
        AirMicrobialSampleSet,
        PutRequest
      >({
        query: (request) => ({
          url: `/samples/air-microbial`,
          method: "PUT",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "SamplesAirMicrobialSampleSet", id: request.id },
          "SamplesAirMicrobialSampleSet",
          "SamplesSampleViewModel",
          // The backend derives the results of the report assessments of
          // the sample from its values, see SampleJudgementChanged.
          "HtzReport",
          "HtzSummary",
        ],
      }),
      samplesAirMicrobialSampleSetUpdateLab: build.mutation<
        AirMicrobialSampleSet,
        UpdateLabRequest
      >({
        query: (request) => ({
          url: `/samples/air-microbial/lab`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "SamplesAirMicrobialSampleSet", id: request.id },
          "SamplesAirMicrobialSampleSet",
          "SamplesSampleViewModel",
        ],
      }),
      samplesAirMicrobialSampleSetUpdateReference: build.mutation<
        AirMicrobialSampleSet,
        UpdateReferenceRequest
      >({
        query: (request) => ({
          url: `/samples/air-microbial/reference`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "SamplesAirMicrobialSampleSet", id: request.id },
          "SamplesAirMicrobialSampleSet",
          // The backend derives the results of the report assessments of
          // the sample from its values, see SampleJudgementChanged.
          "HtzReport",
          "HtzSummary",
        ],
      }),
      samplesAirMicrobialSampleSetShow: build.query<
        AirMicrobialSampleSet,
        ShowRequest
      >({
        query: (request) => ({
          url: `/samples/air-microbial`,
          method: "GET",
          params: request,
        }),
        providesTags: providesEntity("SamplesAirMicrobialSampleSet"),
      }),
      samplesAirMicrobialSampleSetList: build.query<List, ListRequest>({
        query: (request) => ({
          url: `/samples/air-microbial/list`,
          method: "GET",
          params: {
            request: btoa(JSON.stringify(request)),
          },
        }),
        providesTags: (result) =>
          providesList(result?.sampleSets, "SamplesAirMicrobialSampleSet"),
      }),
      samplesAirMicrobialSampleSetDelete: build.mutation<null, DeleteRequest>({
        query: (request) => ({
          url: `/samples/air-microbial`,
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: [
          "SamplesAirMicrobialSampleSet",
          "SamplesSampleViewModel",
        ],
      }),
    }),
  });

export const {
  useSamplesAirMicrobialSampleSetCreateMutation,
  useSamplesAirMicrobialSampleSetPutMutation,
  useSamplesAirMicrobialSampleSetUpdateLabMutation,
  useSamplesAirMicrobialSampleSetUpdateReferenceMutation,
  useSamplesAirMicrobialSampleSetShowQuery,
  useSamplesAirMicrobialSampleSetListQuery,
  useLazySamplesAirMicrobialSampleSetListQuery,
  useSamplesAirMicrobialSampleSetDeleteMutation,
} = samplesAirMicrobialApi;

export type PutRequest = AirMicrobialSampleSet;

export interface UpdateLabRequest {
  id: string;
  labId: EntityId;
}

/**
 * Sets the set the germ count of the set is compared against; null removes
 * the reference. A reference cannot have a reference itself.
 */
export interface UpdateReferenceRequest {
  id: string;
  referenceSetId: EntityId;
}

export interface AirMicrobialSamplePatchRequest extends BaseSamplePatchRequest {
  samplingVolume?: number;
  deviceId?: string;
}

export interface ShowRequest {
  id: string;
}

export type CreateRequest = NewSampleRequest;

export interface ListRequest {
  /**
   * Required by the backend: it must be active and hold at least one
   * company, otherwise the request fails its own validation.
   */
  company: Filter<UUID>;
  lab?: Filter<string>;
  document?: Filter<string>;
  limit?: number;
  offset?: number;
}

export interface List {
  sampleSets: AirMicrobialSampleSet[];
  count: number;
}

export interface DeleteRequest {
  id: string;
}

/**
 * Patches the cached air microbial sample set for an optimistic update. Defined here, at module
 * scope, so it keeps one identity for the whole app — see {@link updateCache}.
 */
export const updateAirMicrobialSampleSetCache = updateCache(
  samplesAirMicrobialApi,
  "samplesAirMicrobialSampleSetShow",
);
